import { useNavigate } from "react-router-dom"; 
import React, { useState, useEffect } from 'react'
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon'; 
import axios from 'axios';

import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import { Select, FormControl, MenuItem} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Button from '@mui/material/Button';
import LoadingOverlay from 'react-loading-overlay-ts';
import MuiDialogAlert from '../components/MuiDialogAlert';

import { 
    getEpochForDayInTimezone, 
    getStartEndMonthEpoch, 
    epochToHumanReadableTS, 
    convertLuxonDateTimeToSqlFormat 
} from '../components/Functions'


import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    TimeScale,
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    TimeScale,
    Title,
    Tooltip,
    Legend
);

const form_target_list = [
    "device_id",
    "property",
    "filter",
    "date_1",
    "date_2"
]

const col_space = "10px"

const datepicker_config = {
    "day": {
        "date_1": {
            "description": "Select Date",
            "view": ['year', 'month', 'day'],
            "hidden": false
        },
        "date_2": {
            "description" : "",
            "view": ['year', 'month', 'day'],
            "hidden": true
        }        
    },
    "month": {
        "date_1": {
            "description": "Select Month/Year",
            "view": ['month', 'year'],
            "hidden": false
        },
        "date_2": {
            "description": "",
            "view": ['month', 'year'],
            "hidden": true
        }
    },
    "year": {
        "date_1": {
            "description": "Start Month/Year",
            "view": ['month', 'year'],
            "hidden": false
        },
        "date_2": {
            "description": "End Month/Year",
            "view": ['month', 'year'],
            "hidden": false
        }
    }
}

const list_view_cols_presets={
    "volume":[
        { field: 'timestamp', headerName: 'Timestamp', width: 200, headerClassName: 'datagrid_header_default' },
        { field: 'location', headerName: 'Location', width: 200, headerClassName: 'datagrid_header_default' },
        { field: 'vol_height', headerName: 'Vol Height', width: 200, headerClassName: 'datagrid_header_default' },
        { field: 'vol_percent', headerName: 'Vol (%)', width: 200, headerClassName: 'datagrid_header_default' }
    ],
    "temperature":[
        { field: 'timestamp', headerName: 'Timestamp', width: 200, headerClassName: 'datagrid_header_default' },
        { field: 'location', headerName: 'Location', width: 200, headerClassName: 'datagrid_header_default' },
        { field: 'temperature', headerName: 'Temperature', width: 200, headerClassName: 'datagrid_header_default' }
    ],
    "batt_lvl":[
        { field: 'timestamp', headerName: 'Timestamp', width: 200, headerClassName: 'datagrid_header_default' },
        { field: 'location', headerName: 'Location', width: 200, headerClassName: 'datagrid_header_default' },
        { field: 'batt_lvl', headerName: 'Battery (%)', width: 200, headerClassName: 'datagrid_header_default' }
    ]

}

const timeZone = 'Asia/Singapore'; // Your desired timezone


const Trends = () => {  // Main Function

    let navigate = useNavigate();


    // 1
    const [formData, setFormData] = useState({
        "device_id": "",
        "property": "volume",
        "filter": "day",
        "date_1": "",
        "date_2": ""
    })
    // 2
    const [loadingActive, setLoadingActive] = useState(false);
    // 3
    const [deviceList, setDeviceList] = useState([]);
    // 4
    const [datePickerConfig, setDatePickerConfig] = useState({
        "date_1": {
            "description": "",
            "view": ['year', 'month', 'day'],
            "hidden": true
        },
        "date_2": {
            "description": "",
            "view": ['year', 'month', 'day'],
            "hidden": true
        }
    });
    // 5
    const [graphData, setGraphData] = useState({
        datasets: [
            {
                label: 'Value',
                data: [{"x":0,"y":0}],
                fill: false,
                borderColor: 'rgba(75,192,192,1)',
                tension: 0.1,
            }
        ]
    });
    // 6
    const [graphOptions, setGraphOptions] = useState({
        scales: {
            x: {
                type: 'time',
                time: {
                    // Luxon format string
                    tooltipFormat: 'HH:mm:ss',
                    unit: 'hour'
                },
                title: {
                    display: true,
                    text: 'Date 123'
                },
                min: 1703952000000,
                max: 1704038340000
            },
            y: {
                title: {
                    display: true,
                    text: 'Volume Percentage',
                },
            },
        }
    });
    // 7
    const [trendTitle, setTrendTitle] = useState("");
    // 8
    const [listViewCols, setListViewCols] = useState([
        { field: 'loading', headerName: 'Loading' },
    ]);
    // 9
    const [listViewData, setListViewData] = useState([])
    // 10
    const [trendView, setTrendView] = useState({
        "data_container_div_class": "hidden",
        "graph_view_btn_class": "trend_view_buttons_active",
        "list_view_btn_class": "trend_view_buttons",
        "graph_view_div_class": "",
        "list_view_div_class": "hidden",
    })
    // 11
    const [disableLoadBtn, setDisableLoadBtn] = useState(true)

    const [modalHeader, setModalHeader] = useState("");
    const [modalBody, setModalBody] = useState("");
    const [modalHtml, setModalHtml] = useState("");
    const [showModal, setShowModal] = useState(false);

    const handleClose = () => setShowModal(false);

    const currentDate = DateTime.local();

    useEffect(() => { // Load data on page Start up 

        const getDevicesData = async () => {
            try {
                // Make a GET request using Axios
                let url = process.env.REACT_APP_API_SERVER + "/getDevicesData";
                console.log(`URL: ${url}`);
                const response = await axios.get(url, {
                    params: {
                        "api_user": JSON.parse(localStorage.getItem('user'))
                    }
                });

                if (response.data.status === "API_AUTH_ERROR") {
                    // Force user to re-login if unable to autheticate with the API
                    navigate("/logout")
                }


                console.log(response.data);
                // Set the retrieved data to the state

                let data = response.data;
                let tmp_array = []
                for (let index = 0; index < data.length; index++) {
                    const d = data[index];
                    let tmp_json = {}
                    tmp_json.device_id = d.device_id;
                    tmp_json.description = d.settings.description
                    tmp_array.push(tmp_json);
                }

                setDeviceList(tmp_array)
                updateFormData("device_id", tmp_array[0].device_id)
                let val = convertLuxonDateTimeToSqlFormat(currentDate)
                updateFormData("date_1", val)
                updateFormData("date_2", val)

            } catch (error) {
                // Handle errors if the request fails
                console.log(error);
                setModalHeader("Error")
                setModalBody("Unable to load device settings data")
                setShowModal(true) 
            }
        };


        setLoadingActive(true);
        getDevicesData()
        setDatePickerConfig(datepicker_config.day)
        setLoadingActive(false);


    }, [])

    useEffect(() => { // Change the type of dates that the user can choose based on the "filter by" 
        setDatePickerConfig(datepicker_config[formData.filter])
    },[formData.filter])

    useEffect(() => { // Change the type of dates that the user can choose based on the "filter by" 
        if (formData.filter == "day" || formData.filter == "month"){
            if (formData.date_1 != ""){
                setDisableLoadBtn(false)
            }
        }

        if (formData.filter == "year"){
            if (formData.date_1 != "" && formData.date_2 != ""){
                setDisableLoadBtn(false)
            }
        }        
    }, [formData.date_1,formData.date_2])


    function updateForm(e) {
        console.log(e)

        if (form_target_list.includes(e.target.id)) {
            updateFormData(e.target.id, e.target.value)
        }

        if (form_target_list.includes(e.target.name)) {
            updateFormData(e.target.name, e.target.value)
        }
        return
        
    }

    function updateFormDate(id){
        return function (e, newValue) {
            console.log(id)
            console.log(e);

            let year = e.c.year
            let month
            let day

            if (Number(e.c.month) < 10){
                month = "0" + e.c.month
            }
            else{
                month = e.c.month
            }

            if (Number(e.c.day) < 10) {
                day = "0" + e.c.day
            }
            else {
                day = e.c.day
            }

            let val = `${year}-${month}-${day}`
            updateFormData(id, val)
        };
    }
    
    function updateFormData(id, val) {
        setFormData((prevFormData) => ({
            ...prevFormData,
            [id]: val,
        }));
    }
    

    function load_btn_click() {
        let url = process.env.REACT_APP_API_SERVER + "/getTrendData";
        let post_data = formData
        post_data.api_user = JSON.parse(localStorage.getItem('user'))

        setLoadingActive(true)

        // Change graph options based on formData
        let val = genGraphOptions()
        setGraphOptions((prevData) => ({
            ...prevData,
            scales: val.scales
        }));

        // Change List View based on formData
        setListViewCols(list_view_cols_presets[formData.property])

        axios.post(url,post_data)
            .then(function (response) {
            let data = response.data;
            console.log(data)

            let tmp_array_graph = []
            let tmp_array_list = []

            // Create dataset for graph view & List view
            for (let index = 0; index < data.length; index++) {
                const d = data[index];
                let tmp_json_graph = {}
                let tmp_json_list = {}

                tmp_json_graph.x = d.epoch * 1000

                tmp_json_list.id = d.id
                tmp_json_list.timestamp = epochToHumanReadableTS(d.epoch)
                tmp_json_list.location = getLocationDescription(d.device_id)
                

                if (formData.property === "volume") {
                    // Case 1: Volume reading has no errors
                    if (d.data.vol_percent >= 0){
                        if (d.data.vol_percent > 100) { // If greater than 100% then just clip it down to 100%
                            tmp_json_graph.y = 100
                            tmp_json_list.vol_percent = "100%"
                        }
                        else {
                            tmp_json_graph.y = d.data.vol_percent
                            tmp_json_list.vol_percent = d.data.vol_percent + "%"
                        }
                        tmp_json_list.vol_height = d.data.vol_height + "mm"
                    }
                    // Case 2: Volume reading has ERRORS
                    else{
                        tmp_json_list.vol_percent = "ERROR"
                        tmp_json_list.vol_height = "ERROR"
                    }

                }

                if (formData.property === "batt_lvl") {
                    if (d.data.batt_percent > 100) {
                        tmp_json_graph.y = 100
                        tmp_json_list.batt_lvl = "100%"
                    }
                    else {
                        tmp_json_graph.y = d.data.batt_percent
                        tmp_json_list.batt_lvl = d.data.batt_percent + "%"
                    }
                }

                if (formData.property === "temperature") {
                    tmp_json_graph.y = d.data.temperature
                    tmp_json_list.temperature = d.data.temperature + "°C"

                }


                tmp_array_graph.push(tmp_json_graph)
                tmp_array_list.push(tmp_json_list)

                console.log(tmp_json_list)
            }

            let graph_data = {
                datasets: [
                    {
                        label: 'Value',
                        data: tmp_array_graph,
                        fill: false,
                        borderColor: 'rgba(75,192,192,1)',
                        tension: 0.1,
                    }
                ]
            }

            console.log(tmp_array_list)

            setGraphData(graph_data)
            setListViewData(tmp_array_list)

            setTrendView((prevData) => ({
                ...prevData,
                data_container_div_class: ""
            }));

            setLoadingActive(false)
        })
        .catch(function (error) {
            // setModalHeader("Error: Unable to Generate Report")
            // setModalBody(error)
            // setShowModal(true)
            setLoadingActive(false)
        });

    }

    function graph_view_btn_click() {
        setTrendView({      
            "data_container_div_class": "",      
            "graph_view_btn_class": "trend_view_buttons_active",
            "list_view_btn_class": "trend_view_buttons",
            "graph_view_div_class": "",
            "list_view_div_class": "hidden"
        })
    }

    function list_view_btn_click() {
        setTrendView({
            "data_container_div_class": "",      
            "graph_view_btn_class": "trend_view_buttons",
            "list_view_btn_class": "trend_view_buttons_active",
            "graph_view_div_class": "hidden",
            "list_view_div_class": ""
        })    
    }

    function genGraphOptions(){
        let tmp_json = graphOptions

        if (formData.property == "volume"){
            tmp_json.scales.y.min = 0
            tmp_json.scales.y.max = 100
            tmp_json.scales.y.title.text = "Volume Percent"
            setTrendTitle("VOLUME")
        }

        if (formData.property == "batt_lvl"){
            tmp_json.scales.y.min = 0
            tmp_json.scales.y.max = 100
            tmp_json.scales.y.title.text = "Batt Percent"
            setTrendTitle("BATTERY")
        }

        if (formData.property == "temperature"){
            tmp_json.scales.y.min = 15
            tmp_json.scales.y.max = 50
            tmp_json.scales.y.title.text = "Temperature (°C)"
            setTrendTitle("TEMPERATURE")
        }

        if (formData.filter == "day"){
            let tmp = getEpochForDayInTimezone(formData.date_1, timeZone);
            let startEpoch = tmp.startOfDayEpoch
            let endEpoch = tmp.endOfDayEpoch
            
            tmp_json.scales.x.time.unit = "hour"
            tmp_json.scales.x.min = startEpoch * 1000
            tmp_json.scales.x.max = endEpoch * 1000
            tmp_json.scales.x.title.text = "Day"
        }

        if (formData.filter == "month"){
            let tmp = getStartEndMonthEpoch(formData.date_1, timeZone);
            let startEpoch = tmp.epochMonthStart
            let endEpoch = tmp.epochMonthEnd
            
            tmp_json.scales.x.time.unit = "day"
            tmp_json.scales.x.min = startEpoch * 1000
            tmp_json.scales.x.max = endEpoch * 1000
            tmp_json.scales.x.title.text = "Month"
        }

        if (formData.filter == "year"){
            let date_1 = getStartEndMonthEpoch(formData.date_1, timeZone);
            let date_2 = getStartEndMonthEpoch(formData.date_2, timeZone);
            let startEpoch = date_1.epochMonthStart
            let endEpoch = date_2.epochMonthEnd
            
            tmp_json.scales.x.time.unit = "month"
            tmp_json.scales.x.min = startEpoch * 1000
            tmp_json.scales.x.max = endEpoch * 1000
            tmp_json.scales.x.title.text = "Year"
        }

        return tmp_json

    }

    function getLocationDescription(device_id){
        for (var i = 0; i < deviceList.length; i++){
            if (deviceList[i].device_id === device_id){
                return deviceList[i].description
            }
        }
    }



    return (
        <main>
            <div align="left" style={{ height: "100%" }}>
                <LoadingOverlay
                    active={loadingActive}
                    spinner
                    text="Loading ..."
                    className="spinner_div"
                >

                <div className='page_title'>Trends</div>

                <div>
                    <table>
                        <tr>
                            {/* Select: Device */}
                            <td width="150px"> 
                                <div className="form-labels">Device</div>
                                <FormControl fullWidth>
                                    <Select
                                        id="device_id"
                                        name="device_id"
                                        label="Select Device"
                                        value={formData.device_id}
                                        variant="outlined"
                                        onChange={updateForm}
                                        sx={{
                                            '& legend': { display: 'none' },
                                            '& fieldset': { top: 0 },
                                            ' & .MuiSelect-select': { backgroundColor: "#FFFFFF", fontSize: 14 }
                                        }}
                                    >
                                        {
                                            deviceList.map(device => {
                                                return <MenuItem key={device.device_id} value={device.device_id}>
                                                    {device.description}
                                                </MenuItem>
                                            })
                                        }
                                    </Select>
                                </FormControl>
                            </td>
                            <td width={col_space}></td>
                            {/* Select: Property */}
                            <td width="150px">
                                <div className="form-labels">Property</div>
                                <FormControl fullWidth>
                                    <Select
                                        id="property"
                                        name="property"
                                        label="Select property"
                                        value={formData.property}
                                        variant="outlined"
                                        onChange={updateForm}
                                        sx={{
                                            '& legend': { display: 'none' },
                                            '& fieldset': { top: 0 },
                                            ' & .MuiSelect-select': { backgroundColor: "#FFFFFF", fontSize: 14 }
                                        }}
                                    >
                                    <MenuItem key="volume" value="volume">VOLUME</MenuItem>
                                    <MenuItem key="temperature" value="temperature">TEMPERATURE</MenuItem>
                                    <MenuItem key="batt_lvl" value="batt_lvl">BATT LVL</MenuItem>
                                    </Select>
                                </FormControl>
                            </td>
                                <td width={col_space}></td>
                            {/* Select: Filter By */}
                            <td width="150px">
                                <div className="form-labels">Filter By</div>
                                <FormControl fullWidth>
                                    <Select
                                        id="filter"
                                        name="filter"
                                        label="Filter By"
                                        value={formData.filter}
                                        variant="outlined"
                                        onChange={updateForm}
                                        sx={{
                                            '& legend': { display: 'none' },
                                            '& fieldset': { top: 0 },
                                            ' & .MuiSelect-select': { backgroundColor: "#FFFFFF", fontSize: 14 }
                                        }}
                                    >
                                    <MenuItem key="day" value="day">Day</MenuItem>
                                    <MenuItem key="month" value="month">Month</MenuItem>
                                    <MenuItem key="year" value="year">Year</MenuItem>
                                    </Select>
                                </FormControl>
                            </td>
                                <td width={col_space}></td>
                            {/* DatePicker: Date_1 */}
                            <td>
                                <div className={datePickerConfig.date_1.hidden ? "hidden" : ""}>
                                    <div className="form-labels">{datePickerConfig.date_1.description}</div>
                                    <DatePicker
                                        id="date_1"
                                        name="date_1"
                                        type="date"
                                        views={datePickerConfig.date_1.view}
                                        onChange={updateFormDate("date_1")}
                                        defaultValue={currentDate}
                                        sx={{
                                            '& legend': { display: 'none' },
                                            '& fieldset': { top: 0 },
                                            '& .MuiInputBase-root': { backgroundColor: "#FFFFFF", fontSize: 14 }
                                        }}
                                    />
                                </div>
                            </td>
                                <td width={col_space}></td>
                            {/* DatePicker: Date_2 */}
                            <td>
                                <div className={datePickerConfig.date_2.hidden ? "hidden" : ""}>
                                    <div className="form-labels">{datePickerConfig.date_2.description}</div>
                                    <DatePicker
                                        id="date_2"
                                        name="date_2"
                                        type="date"
                                        views={datePickerConfig.date_2.view}
                                        onChange={updateFormDate("date_2")}
                                        defaultValue={currentDate}
                                        sx={{
                                            '& legend': { display: 'none' },
                                            '& fieldset': { top: 0 },
                                            '& .MuiInputBase-root': { backgroundColor: "#FFFFFF", fontSize: 14 }
                                        }}
                                    />
                                </div>
                            </td>
                                <td width={col_space}></td>
                            {/* Button: Load */}
                            <td>
                                <div style={{paddingTop: 28}}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={load_btn_click}
                                        sx={{ 
                                            width: 100, 
                                            height: 50,
                                            "&.Mui-disabled": {
                                                background: "#ffffff4d"
                                            }
                                        }}
                                        disabled={disableLoadBtn}
                                    >
                                        Load
                                    </Button>
                                </div>
                            </td>
                        </tr>
                    </table>
                </div>

                <div style={{height: "20px"}}></div>
                <div style={{height: "1px", backgroundColor: "#FFFFFF", width:"1200px"}}></div>
                <div style={{height: "20px"}}></div>

                    <div className={'data_container' + " " + trendView.data_container_div_class}>
                    <div className='data_container_header'>
                        <table width="100%" height="100%" border="0">
                            <tr>
                                <td>{trendTitle}</td>

                                <td className={trendView.list_view_btn_class}>
                                    <div align='center'>
                                        <i className="bi bi-list" style={{ paddingRight: "5px" }}></i> 
                                            <Button
                                                onClick={list_view_btn_click}
                                                sx={{ 
                                                    padding: "0px", 
                                                    color:"#FFFFFF",
                                                    fontFamily: "Roboto', sans-serif",
                                                    fontSize: "20px"
                                                }}
                                            >
                                                List View
                                            </Button>
                                    </div>
                                </td>
                                <td width="20px"></td>
                                    <td className={trendView.graph_view_btn_class}>
                                    <div align='center'>
                                        <i className="bi bi-graph-up" style={{ paddingRight: "5px" }}></i> 
                                            <Button
                                                onClick={graph_view_btn_click}
                                                sx={{
                                                    padding: "0px",
                                                    color: "#FFFFFF",
                                                    fontFamily: "Roboto', sans-serif",
                                                    fontSize: "20px"
                                                }}
                                            >
                                                Graph View
                                            </Button>                                    
                                    </div>
                                </td>
                            </tr>
                        </table>
                    </div>
                    <div className={'graph_content' + " " + trendView.graph_view_div_class}>
                        <Line data={graphData} options={graphOptions} />
                    </div>
                        <div className={'list_content' + " " + trendView.list_view_div_class}>
                            <div style={{ width: "100%" }}>
                                <DataGrid
                                    className='datagrid_default'
                                    rows={listViewData}
                                    columns={listViewCols}
                                    pageSize={50}
                                    rowsPerPage={25} // Set the default number of rows per page here
                                    // onRowClick={transactionsDataRowClick}
                                    disableVirtualization
                                    disableSelectionOnClick
                                    components={{ Toolbar: GridToolbar }}
                                    sx={{
                                        '& .MuiTablePagination-selectLabel': {
                                            paddingTop: '15px'

                                        },
                                        '& .MuiTablePagination-displayedRows': { paddingTop: '15px' },
                                        '& .MuiDataGrid-cell': { fontSize: 14 },
                                        '& .MuiDataGrid-columnHeader': {
                                            fontSize: 16,
                                            color: '#1B9982',
                                            fontWeight: 900
                                        },
                                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell--editing': {
                                            outline: 'none', // Remove outline when cell is focused or editing
                                            border: 'none', // Remove border when cell is focused or editing
                                        },
                                        '& .MuiDataGrid-root': {
                                            border: 'none'
                                        },
                                        '& .MuiButton-root, & .MuiButtonBase-root': {
                                            color: '#1B9982', // Change this to your desired color
                                        },
                                        '& .MuiTablePagination-root': {
                                            color: '#FFFFFF', // Change this to your desired color
                                        },


                                    }} />
                            </div>

                    </div>
                </div>


                    <MuiDialogAlert
                        showModal={showModal}
                        handleClose={handleClose}
                        html={modalHtml}
                        bodyContent={modalBody}
                        title={modalHeader}
                        bodyClass="whitespace"
                    />

                </LoadingOverlay>
            </div>
        </main>
    )
}

export default Trends